import { HttpClient, HttpResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { AppConfig, buildApiUrl } from '@app/app.config';
import { Observable } from 'rxjs';
import { OriginInfo, QuoteRequest, ResponseBulkImport, ResponseBulkImportConfirm, ResponseBulkImportQuote, Row } from './utils-bulk-upload';
import { tap } from 'rxjs';
import { formatPriceAr } from '../shipments/utils-shipments';
import type { BulkUploadV2Response } from '../shipments/shipments-form-v2/bulk-upload-v2-modal.component';

@Injectable({
  providedIn: 'root'
})
export class BulkUploadService {
  private http = inject(HttpClient);

  private bulkImportContent = signal<ResponseBulkImport | null>(null);
  private originInfoSnapshot = signal<OriginInfo | null>(null);
  private shipmentsConfirmed = signal<ResponseBulkImportConfirm | null>(null);

  public bulkImport = computed(() => this.bulkImportContent());
  public originInfo = computed(() => this.originInfoSnapshot());
  public paymentId = computed(() => this.shipmentsConfirmed()?.paymentId ?? null);
  public confirmedShipmentQuoteIds = computed(() => (this.shipmentsConfirmed()?.confirmedQuotes ?? []).map(shipmentQuote => shipmentQuote.shipmentQuoteId));
  public resumePrices = computed(() => {
    const res = this.shipmentsConfirmed();
    if (!res) return null;
    const subtotal = res.confirmedQuotes.reduce((acc, shipmentQuote) => acc + shipmentQuote.subTotal, 0);
    return {
      subtotal: formatPriceAr(subtotal),
      total: formatPriceAr(res.totalAmount),
      taxes: formatPriceAr(res.totalAmount - subtotal),
    };
  });

  public goodShipments = computed(() => {
    const content = this.bulkImportContent();
    return content ? content.rows.filter(r => r.status === 'Valid') : [];
  });

  public badShipments = computed(() => {
    const content = this.bulkImportContent();
    return content ? content.rows.filter(r => r.status === 'Invalid') : [];
  });


  setBulkImport(value: ResponseBulkImport | null): void {
    this.bulkImportContent.set(value);
  }

  setOriginInfo(value: OriginInfo | null): void {
    this.originInfoSnapshot.set(value);
  }

  setShipmentsConfirmed(value: ResponseBulkImportConfirm | null): void {
    this.shipmentsConfirmed.set(value);
  }

  clearBulkImport(): void {
    this.bulkImportContent.set(null);
    this.originInfoSnapshot.set(null);
    this.shipmentsConfirmed.set(null);
  }

  getLatestBulkImport(): ResponseBulkImport | null {
    return this.bulkImportContent();
  }

  getPaymentId(): string | null {
    return this.paymentId();
  }

  getShipmentQuoteIds(): number[] {
    return this.confirmedShipmentQuoteIds();
  }

  uploadBulkImport(file: File, originInfo: OriginInfo, forced = true): Observable<ResponseBulkImport> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('originInfo', JSON.stringify(originInfo));

    const url = buildApiUrl(`${AppConfig.postBulkImport}?forced=${forced}`);
    return this.http.post<ResponseBulkImport>(url, formData, {
      withCredentials: true
    }).pipe(
      tap((res) => {
        this.setBulkImport(res);
        this.setOriginInfo(originInfo);
      })
    );
  }

  uploadBulkImportV2(file: File): Observable<BulkUploadV2Response> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<BulkUploadV2Response>(buildApiUrl(AppConfig.postBulkImportV2), formData, {
      withCredentials: true
    });
  }
  
  refreshBulkImport() {
    return this.http.get<ResponseBulkImport>(buildApiUrl(AppConfig.getBulkImport), { withCredentials: true })
    .subscribe({
      next: (res) => {
        this.setBulkImport(res);
      }
    })
  }
  
  updateBulkImport(data: Row[]) {
    return this.http.put<ResponseBulkImport>(buildApiUrl(AppConfig.putBulkImport), data, { withCredentials: true })
    .subscribe({
      next: (res) => {
        this.setBulkImport(res);
      }
    });
  }

  deleteShippings(data: unknown[]) {
    return this.http.put<ResponseBulkImport>(buildApiUrl(AppConfig.putBulkImport), data, { withCredentials: true })
    .subscribe({
      next: (res) => {
        this.setBulkImport(res);
      }
    });
  }

  getTemplateBulkImport(): Observable<HttpResponse<Blob>> {
    return this.http.get(buildApiUrl(AppConfig.getTemplateBulkImport), {
      observe: 'response',
      responseType: 'blob',
      withCredentials: true
    });
  }

  getManualBulkImport(): Observable<HttpResponse<Blob>> {
    return this.http.get(buildApiUrl(AppConfig.getManualBulkImport), {
      observe: 'response',
      responseType: 'blob',
      withCredentials: true
    });
  }

  getPickitPointsBulkImport(): Observable<HttpResponse<Blob>> {
    return this.http.get(buildApiUrl(AppConfig.getPickitPointsBulkImport), {
      observe: 'response',
      responseType: 'blob',
      withCredentials: true
    });
  }

  postBulkImportQuote(): Observable<ResponseBulkImportQuote> {
    return this.http.post<ResponseBulkImportQuote>(buildApiUrl(AppConfig.postBulkImportQuote), {}, { 
      withCredentials: true 
    });
  }

  postConfirmShipmentsQuote(data: QuoteRequest[]): Observable<ResponseBulkImportConfirm> {
    return this.http.post<ResponseBulkImportConfirm>(buildApiUrl(AppConfig.postBulkImportConfirmQuote), data, { 
      withCredentials: true 
    }).pipe(
      tap((res) => {
        this.setShipmentsConfirmed(res);
      })
    );
  }
}

